import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Briefcase, Users2, Landmark, Shield, Crown, Layers } from 'lucide-react';
export function SolutionsPage() {
  const solutions = [{
    icon: <GraduationCap size={28} data-id="element-2046" data-mp="1rel97hzkmr82" />,
    title: 'For Education',
    color: 'bg-teal',
    link: '#education',
    description: 'SyNeural can support schools, universities, and learning systems by strengthening reasoning, emotional regulation, media literacy, self-direction, and long-term capability development. At early stages, the framework can help identify developmental bottlenecks and connect them to support pathways. In later stages, it can help students build stronger logic, planning ability, consequential thinking, and resilience against manipulation. This makes the platform relevant not only for academic performance, but for the broader development of readiness, responsibility, and civic capability.'
  }, {
    icon: <Briefcase size={28} data-id="element-2047" data-mp="1ywry6c2okdzr" />,
    title: 'For Employers and Workforce Programs',
    color: 'bg-navy-light',
    link: '#employers',
    description: 'In workforce and organizational environments, SyNeural can help strengthen execution, communication, advancement readiness, decision quality, and leadership development. The platform can be used to identify where individuals or teams need stronger planning, reasoning, discipline, or stress tolerance, then connect those insights to practical pathways for growth. This makes it useful for talent development, internal mobility, performance support, and leadership pipelines.'
  }, {
    icon: <Users2 size={28} data-id="element-2048" data-mp="2rkxf0nqm5iub" />,
    title: 'For Civil Society',
    color: 'bg-green-soft',
    link: '#civil-society',
    description: 'Civil society organizations, nonprofits, mentorship networks, and community-based institutions can use SyNeural to strengthen misinformation resilience, local leadership, opportunity alignment, and community capability-building. The platform can help identify where support systems are weak, where growth potential is underdeveloped, and where communities need stronger pathways for participation, self-direction, and constructive engagement.'
  }, {
    icon: <Landmark size={28} data-id="element-2049" data-mp="2287wslepty4g" />,
    title: 'For Government and Public Institutions',
    color: 'bg-navy',
    link: '#government',
    description: 'SyNeural can support public institutions through workforce modernization, capability mapping, civic resilience planning, and leadership preparation. Municipal systems can use it to identify local bottlenecks such as weak youth pathways, low workforce readiness, or high misinformation vulnerability. Regional and national systems can use it to support education reform, public-sector development, ministry-level planning, and long-range stability initiatives. This makes the platform relevant not only as a development tool, but as strategic infrastructure for human capability and institutional performance.'
  }, {
    icon: <Crown size={28} data-id="element-2050" data-mp="o3o0whmh5xq4" />,
    title: 'For Leadership Development',
    color: 'bg-teal',
    link: '#leadership',
    description: 'At higher levels of responsibility, SyNeural becomes a more rigorous framework for leadership readiness. The platform can support the evaluation and development of individuals moving into roles where poor judgment carries broad institutional or national consequences. It helps shift leadership development away from image, charisma, or status alone and toward consequence reasoning, stability, foresight, crisis performance, and institutional responsibility.'
  }];
  return <div className="min-h-screen bg-surface-bg" data-id="element-2051" data-mp="t1g2k3cx8y7i">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-2052" data-mp="t3k3ccfixh3k">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-2053" data-mp="3ioosx8ixuiog">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-2054" data-mp="1ab9y5cn7ass9">
            <span className="w-8 h-px bg-teal" data-id="element-2055" data-mp="wj3jjuzp4tux"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-2056" data-mp="wg7n6b3hyd8l">
              Solutions
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-2057" data-mp="1o8ykkowx27zf"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-2058" data-mp="1o7nhkrbap49d">
            Solutions Across the Full Human and Institutional Spectrum
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-2059" data-mp="1fb4nhbrbvab7">
            SyNeural's platform architecture is designed for flexible deployment
            across education, workforce systems, civil society, public
            institutions, and leadership environments.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-2060" data-mp="a4ju3yjrgth">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-2061" data-mp="3sg9t26t7a9t0">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-2062" data-mp="3drdleav22ysx">
            Through CJI and NCJI, the platform helps measure, strengthen, and
            develop judgment in ways that are relevant to each setting, from
            early capability formation to institutional readiness and
            executive-level responsibility.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-2063" data-mp="2v048rspzntx4">
            Rather than offering a one-size-fits-all model, SyNeural adapts its
            framework to the level of responsibility, complexity, and
            consequence involved. At the individual level, it supports growth,
            readiness, and opportunity alignment. At the institutional level, it
            supports capability-building, leadership development, and decision
            quality. At the public-system level, it supports resilience,
            modernization, and long-range planning.
          </p>
        </motion.div>
      </section>

      {/* Three-Level Framework */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-2064" data-mp="b0v6j5lit3a4">
        <div className="max-w-5xl mx-auto" data-id="element-2065" data-mp="fyn7sq8221qz">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2066" data-mp="jzyrkf48zsr9">
            <div className="bg-white p-8 rounded-lg border border-line" data-id="element-2067" data-mp="2ahm1dkpfue18">
              <div className="w-12 h-12 rounded-lg bg-teal/10 flex items-center justify-center text-teal mb-6" data-id="element-2068" data-mp="2ajq7886wdn45">
                <Users2 size={24} data-id="element-2069" data-mp="20s2joidgoq7y" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-2070" data-mp="20qj2mpjzeb54">
                Individual Level
              </h3>
              <p className="text-ink-muted leading-relaxed" data-id="element-2071" data-mp="1woo1bl21vxrc">
                Growth, readiness, and opportunity alignment
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-line" data-id="element-2072" data-mp="1mrjhhrvogedp">
              <div className="w-12 h-12 rounded-lg bg-navy-light/10 flex items-center justify-center text-navy-light mb-6" data-id="element-2073" data-mp="1hsg8xxghnq0z">
                <Landmark size={24} data-id="element-2074" data-mp="1dsxxrtegytbc" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-2075" data-mp="3c6za9wpannhf">
                Institutional Level
              </h3>
              <p className="text-ink-muted leading-relaxed" data-id="element-2076" data-mp="386yyykxhrsel">
                Capability-building, leadership development, and decision
                quality
              </p>
            </div>

            <div className="bg-white p-8 rounded-lg border border-line" data-id="element-2077" data-mp="3vexx5wkul7e9">
              <div className="w-12 h-12 rounded-lg bg-navy/10 flex items-center justify-center text-navy mb-6" data-id="element-2078" data-mp="3hp4575e1q70b">
                <Layers size={24} data-id="element-2079" data-mp="3mmvvxw2yfn2x" />
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-2080" data-mp="3mlcew39h5803">
                Public-System Level
              </h3>
              <p className="text-ink-muted leading-relaxed" data-id="element-2081" data-mp="3cprjwp2tp7zu">
                Resilience, modernization, and long-range planning
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solutions Grid */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-2082" data-mp="33p33jnpyivjs">
        <div className="space-y-20" data-id="element-2083" data-mp="wzep6p1lwf61">
          {solutions.map((solution, index) => <motion.div key={solution.title} initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: index * 0.1
        }} className={`${index % 2 === 0 ? '' : 'lg:flex-row-reverse'} flex flex-col lg:flex-row gap-12 items-start`} data-id="element-2084" data-mp="11xpymynzonnq" data-mpi={index}>
              {/* Icon & Title Column */}
              <div className="lg:w-1/3 flex-shrink-0" data-id="element-2085" data-mp="1puw406g7hrte">
                <div className={`w-16 h-16 rounded-xl ${solution.color} flex items-center justify-center text-white mb-6 shadow-lg`} data-id="element-2086" data-mp="23sudaxfo6y21">
                  {solution.icon}
                </div>
                <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-2087" data-mp="9f1u2c7mnugu">
                  {solution.title}
                </h2>
              </div>

              {/* Description Column */}
              <div className="lg:w-2/3" data-id="element-2088" data-mp="3byfbvt9sbsou">
                <div className="bg-white p-8 lg:p-10 rounded-xl border border-line shadow-sm" data-id="element-2089" data-mp="37viozqd7paf3">
                  <p className="text-lg text-ink-muted leading-relaxed" data-id="element-2090" data-mp="32w659ehn0jgq">
                    {solution.description}
                  </p>
                </div>
              </div>
            </motion.div>)}
        </div>
      </section>

      {/* Why It Matters */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-2091" data-mp="3vmxvlvc0udw5">
        <div className="max-w-4xl mx-auto" data-id="element-2092" data-mp="3qma6xgb9m953">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-2093" data-mp="3lv1vkeqdkdpa">
            <div className="flex items-center gap-4 mb-8" data-id="element-2094" data-mp="degiomumh0gw">
              <div className="w-12 h-12 rounded-lg bg-white/10 flex items-center justify-center" data-id="element-2095" data-mp="kqms12i2sc1f">
                <Layers size={24} data-id="element-2096" data-mp="pp7tf592d1rg" />
              </div>
              <h2 className="text-3xl font-heading font-bold" data-id="element-2097" data-mp="ppznucqxctg5">
                Why the Solutions Model Matters
              </h2>
            </div>

            <p className="text-xl leading-relaxed opacity-90 mb-6" data-id="element-2098" data-mp="zndxnticejzh">
              The strength of the SyNeural platform lies in its ability to adapt
              one unified architecture across very different environments
              without losing coherence.
            </p>

            <p className="text-lg leading-relaxed opacity-80" data-id="element-2099" data-mp="18lzcpeolvgvi">
              The same foundational logic applies throughout: identify
              capability, clarify bottlenecks, connect them to pathways, support
              development, and strengthen readiness over time. That makes the
              platform useful not just for measurement, but for real-world
              growth across the full human and institutional spectrum.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Visual Summary */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-2100" data-mp="3jh5wltbghnvj">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-2101" data-mp="12dhyptzjz5q">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-12 text-center" data-id="element-2102" data-mp="61zsusmcd3bd">
            One Platform, Multiple Applications
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-id="element-2103" data-mp="k0g8j926m45n">
            {[{
            icon: <GraduationCap size={20} data-id="element-2104" data-mp="2lsmbmap1lt88" />,
            label: 'Education',
            link: '#education'
          }, {
            icon: <Briefcase size={20} data-id="element-2105" data-mp="2hsc4gupikwrs" />,
            label: 'Employers',
            link: '#employers'
          }, {
            icon: <Users2 size={20} data-id="element-2106" data-mp="2bonz0xwn7qlu" />,
            label: 'Civil Society',
            link: '#civil-society'
          }, {
            icon: <Landmark size={20} data-id="element-2107" data-mp="1xwrbl75x64vs" />,
            label: 'Government',
            link: '#government'
          }, {
            icon: <Crown size={20} data-id="element-2108" data-mp="1nzd07lyus6ac" />,
            label: 'Leadership',
            link: '#leadership'
          }].map((item, index) => <motion.a key={item.label} href={item.link} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: index * 0.05
          }} className="bg-surface-muted p-6 rounded-lg border border-line text-center hover:border-navy hover:shadow-md transition-all" data-id="element-2109" data-mp="1sw3blvzpqdpf" data-mpi={index}>
                <div className="w-10 h-10 rounded-md bg-white flex items-center justify-center text-navy mx-auto mb-3 border border-line" data-id="element-2110" data-mp="14a28un35maoh">
                  {item.icon}
                </div>
                <div className="text-sm font-medium text-ink-dark" data-id="element-2111" data-mp="vaov3i7zyzxy">
                  {item.label}
                </div>
              </motion.a>)}
          </div>
        </motion.div>
      </section>
    </div>;
}